import React from 'react';
import { Button, DocsAnnotation, Flex, Switch, Text, TagBadge, type RichTextEditorRef } from '@chatsdet/ui';
import type { ActionDataRequirement, ActionInputValues, InlineAssertion } from '@chatsdet/types';
import { ActionInputsEditor } from './ActionInputsEditor';
import { ActionOutputsEditor } from './ActionOutputsEditor';
import { ActionAssertionsEditor } from './ActionAssertionsEditor';
import { MacroRichTextField } from './MacroRichTextField';
import { ConfigSection } from './ConfigSection';
import { AttachmentPickerDialog } from '../../../features/attachments/components/AttachmentPickerDialog';
import { useResolvedAttachments } from '../../../features/attachments/api';
import { getFileTypeBadge } from '../../../features/attachments/utils';
import { formatDistanceToNow } from 'date-fns';

type InferredPageContext = {
  canonicalPageId?: string | null;
  pageTitle?: string | null;
};

type ActionNodeConfigProps = {
  focusKey: number;
  propertyId?: string | null;
  isActive: boolean;
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  inferredPageContext?: InferredPageContext | null;
  onSave?: () => void;
};

export const ActionNodeConfig: React.FC<ActionNodeConfigProps> = ({
  focusKey,
  propertyId: _propertyId,
  isActive: _isActive,
  localData,
  setLocalData,
  inferredPageContext: pageContext,
  onSave,
}) => {
  const titleInputRef = React.useRef<RichTextEditorRef>(null);

  // Auto-focus the title input when node changes (focusKey increments), but only if empty
  React.useEffect(() => {
    const titleText = ((localData.customActionTitle as string | undefined) ?? '').trim();
    if (!titleText) {
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [focusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddRequirement = React.useCallback(
    (requirement: ActionDataRequirement) => {
      setLocalData((prev) => ({
        ...prev,
        dataRequirements: [...((prev.dataRequirements ?? []) as ActionDataRequirement[]), requirement],
      }));
    },
    [setLocalData]
  );

  const handleDeleteRequirement = React.useCallback(
    (name: string) => {
      setLocalData((prev) => {
        const nextRequirements = ((prev.dataRequirements ?? []) as ActionDataRequirement[]).filter(
          (r) => r.name !== name
        );
        const nextInputValues = { ...((prev.inputValues ?? {}) as ActionInputValues) };
        delete nextInputValues[name];
        return {
          ...prev,
          dataRequirements: nextRequirements,
          inputValues: Object.keys(nextInputValues).length > 0 ? nextInputValues : null,
        };
      });
    },
    [setLocalData]
  );

  const clearCapturedRequirements = React.useCallback(() => {
    setLocalData((prev) => ({
      ...prev,
      dataRequirements: [],
      inputValues: null,
      lastRequirementUpdateAt: null,
    }));
  }, [setLocalData]);

  const handleAddOutput = React.useCallback(
    (name: string) => {
      setLocalData((prev) => ({
        ...prev,
        declaredOutputs: [...((prev.declaredOutputs ?? []) as string[]), name],
      }));
    },
    [setLocalData]
  );

  const handleDeleteOutput = React.useCallback(
    (name: string) => {
      setLocalData((prev) => ({
        ...prev,
        declaredOutputs: ((prev.declaredOutputs ?? []) as string[]).filter((n) => n !== name),
        capturedVariableKeys: ((prev.capturedVariableKeys ?? []) as string[]).filter((n) => n !== name),
      }));
    },
    [setLocalData]
  );

  const handleAddAssertion = React.useCallback(
    (assertion: InlineAssertion) => {
      setLocalData((prev) => ({
        ...prev,
        inlineAssertions: [...((prev.inlineAssertions ?? []) as InlineAssertion[]), assertion],
      }));
    },
    [setLocalData]
  );

  const handleUpdateAssertion = React.useCallback(
    (id: string, updates: Partial<InlineAssertion>) => {
      setLocalData((prev) => ({
        ...prev,
        inlineAssertions: ((prev.inlineAssertions ?? []) as InlineAssertion[]).map((a) =>
          a.id === id ? { ...a, ...updates } : a
        ),
      }));
    },
    [setLocalData]
  );

  const handleDeleteAssertion = React.useCallback(
    (id: string) => {
      setLocalData((prev) => ({
        ...prev,
        inlineAssertions: ((prev.inlineAssertions ?? []) as InlineAssertion[]).filter(
          (a) => a.id !== id
        ),
      }));
    },
    [setLocalData]
  );

  const clearAssertions = React.useCallback(() => {
    setLocalData((prev) => ({
      ...prev,
      inlineAssertions: [],
    }));
  }, [setLocalData]);

  const clearOutputs = React.useCallback(() => {
    setLocalData((prev) => ({
      ...prev,
      declaredOutputs: [],
      capturedVariableKeys: [],
    }));
  }, [setLocalData]);

  const clearActionSelection = React.useCallback(() => {
    setLocalData((prev) => ({
      ...prev,
      canonicalActionId: undefined,
      crawlActionId: undefined,
      crawlPageId: undefined,
      actionId: undefined,
      actionKind: undefined,
      actionSelector: undefined,
      actionPriority: undefined,
      actionRole: undefined,
      actionHref: undefined,
      actionDescription: undefined,
      customActionTitle: '',
    }));
  }, [setLocalData]);

  const handleTitleChange = React.useCallback(
    (value: string) => {
      if (!value.trim()) {
        clearActionSelection();
        return;
      }
      setLocalData((prev) => ({
        ...prev,
        canonicalActionId: undefined,
        crawlActionId: undefined,
        crawlPageId: undefined,
        actionId: undefined,
        actionKind: undefined,
        actionSelector: undefined,
        actionPriority: undefined,
        actionRole: undefined,
        actionHref: undefined,
        customActionTitle: value,
      }));
    },
    [clearActionSelection, setLocalData]
  );

  const handleDescriptionChange = React.useCallback(
    (value: string) => {
      if (!value.trim()) {
        setLocalData((prev) => ({
          ...prev,
          actionDescription: undefined,
        }));
        return;
      }
      setLocalData((prev) => ({
        ...prev,
        canonicalActionId: undefined,
        crawlActionId: undefined,
        crawlPageId: undefined,
        actionId: undefined,
        actionKind: undefined,
        actionSelector: undefined,
        actionPriority: undefined,
        actionRole: undefined,
        actionHref: undefined,
        actionDescription: value,
      }));
    },
    [setLocalData]
  );

  const handleRevertGuidance = React.useCallback(() => {
    setLocalData((prev) => ({
      ...prev,
      actionDescription: prev.previousActionDescription,
      previousActionDescription: null,
      guidanceImprovedAt: null,
    }));
  }, [setLocalData]);

  const titleText = (localData.customActionTitle as string | undefined) ?? '';
  const descriptionText = (localData.actionDescription as string | undefined) ?? '';
  const previousActionDescription = (localData.previousActionDescription as string | undefined);
  const guidanceImprovedAt = (localData.guidanceImprovedAt as string | undefined);
  const dataRequirements = (localData.dataRequirements ?? []) as ActionDataRequirement[];
  const inputValues = (localData.inputValues ?? undefined) as ActionInputValues | undefined;
  const attachmentIds = Array.isArray(localData.attachmentIds)
    ? (localData.attachmentIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : [];
  const resolvedAttachments = useResolvedAttachments(attachmentIds);
  const attachments = resolvedAttachments.data?.data ?? [];
  const attachmentFromCaller = localData.attachmentSource === 'from_caller';

  return (
    <div className="space-y-4">
      <MacroRichTextField
        ref={titleInputRef}
        label="Title"
        placeholder="Short label for this action… (type $ for macros)"
        value={titleText}
        onChange={handleTitleChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSave?.();
          }
        }}
        description="Shown on the canvas as the node label."
      />

      <MacroRichTextField
        multiline
        label="Description"
        placeholder={
          pageContext?.pageTitle || pageContext?.canonicalPageId
            ? 'Describe what should happen on this page, including specifics and exact values… (type $ for macros)'
            : 'Describe the action the agent should perform, including specifics and exact values… (type $ for macros)'
        }
        value={descriptionText}
        onChange={handleDescriptionChange}
        description="Used by the agent/runner to understand what to do."
        rows={6}
      />

      <Switch
        size="sm"
        isSelected={localData.alwaysUseAgent ?? false}
        onChange={(isSelected) => setLocalData((prev) => ({ ...prev, alwaysUseAgent: isSelected }))}
        label={<Text size="xs" className="text-muted-foreground">Always use agent</Text>}
      />


      {previousActionDescription && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20 p-3 space-y-2">
          <Flex justify="between" align="center">
            <div>
              <Text size="sm" weight="medium" className="text-blue-700 dark:text-blue-400">
                Guidance improved by agent
              </Text>
              {guidanceImprovedAt && (
                <Text size="xs" className="text-muted-foreground">
                  {formatDistanceToNow(new Date(guidanceImprovedAt))} ago
                </Text>
              )}
            </div>
          </Flex>
          <details className="group">
            <summary className="cursor-pointer text-xs text-blue-600 dark:text-blue-400 hover:underline">
              View original
            </summary>
            <Text size="sm" className="mt-2 whitespace-pre-wrap bg-background/50 p-2 rounded border text-muted-foreground">
              {previousActionDescription}
            </Text>
          </details>
          <Flex justify="end">
            <Button variant="ghost" size="sm" onPress={handleRevertGuidance}>
              Revert to original
            </Button>
          </Flex>
        </div>
      )}

      {localData.actionSelector && (
        <div className="p-3 rounded-lg bg-muted/50">
          <Text className="text-xs text-muted-foreground">
            Selector: {localData.actionSelector}
          </Text>
        </div>
      )}

      <div className="mt-2 space-y-0">
        <DocsAnnotation slug="reference/inline-assertions" title="Inline Assertions">
          <ConfigSection
            title="Assertions"
            count={((localData.inlineAssertions as InlineAssertion[]) ?? []).length}
          >
            <ActionAssertionsEditor
              assertions={(localData.inlineAssertions as InlineAssertion[]) ?? []}
              onAdd={handleAddAssertion}
              onUpdate={handleUpdateAssertion}
              onDelete={handleDeleteAssertion}
              onClearAll={clearAssertions}
            />
          </ConfigSection>
        </DocsAnnotation>

        <DocsAnnotation slug="reference/macros" title="Dynamic Data Macros">
          <ConfigSection title="Inputs" count={dataRequirements.length}>
            <ActionInputsEditor
              dataRequirements={dataRequirements}
              inputValues={inputValues}
              lastRequirementUpdateAt={localData.lastRequirementUpdateAt as string | undefined}
              onChange={(updater) =>
                setLocalData((prev) => ({
                  ...prev,
                  inputValues: updater(prev.inputValues as ActionInputValues | undefined),
                }))
              }
              onAddRequirement={handleAddRequirement}
              onDeleteRequirement={handleDeleteRequirement}
              onClearAll={clearCapturedRequirements}
            />
          </ConfigSection>
        </DocsAnnotation>

        <ConfigSection
          title="Outputs"
          count={
            ((localData.declaredOutputs as string[]) ?? []).length +
            ((localData.capturedVariableKeys as string[]) ?? []).filter(
              (k: string) => !((localData.declaredOutputs as string[]) ?? []).includes(k)
            ).length +
            (localData.captureAudio ? 1 : 0)
          }
        >
          <ActionOutputsEditor
            declaredOutputs={(localData.declaredOutputs as string[]) ?? []}
            capturedVariableKeys={(localData.capturedVariableKeys as string[]) ?? []}
            onAddOutput={handleAddOutput}
            onDeleteOutput={handleDeleteOutput}
            onClearAll={clearOutputs}
          />
          <div className="mt-3 pt-3 border-t border-border/40">
            <Switch
              size="sm"
              isSelected={localData.captureAudio ?? false}
              onChange={(isSelected) =>
                setLocalData((prev) => ({ ...prev, captureAudio: isSelected }))
              }
              label={
                <Text size="xs" className="text-muted-foreground">
                  Capture audio playback
                </Text>
              }
            />
            <Text size="xs" className="text-muted-foreground/70 mt-1 block">
              Records anything the page plays while this action runs. Available to a downstream
              audio-assertion node and as a downloadable run artifact.
            </Text>
          </div>
        </ConfigSection>

        <ConfigSection
          title="Attachments"
          count={attachmentFromCaller ? 0 : attachmentIds.length}
          defaultExpanded={attachmentFromCaller ? true : undefined}
        >
          {attachmentFromCaller ? (
            <Text size="sm" className="text-muted-foreground">
              Supplied by the calling seed node — this step uploads whatever file the seed node
              attaches. Set in the spec (<code>attachmentSource: from_caller</code>); remove that
              bullet there to pick library attachments again. Library attachments are ignored
              while it is set.
            </Text>
          ) : (
            <div>
              {attachmentIds.length > 0 && (
                <Flex align="center" justify="end" className="mb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => setLocalData((prev) => ({ ...prev, attachmentIds: [] }))}
                    className="text-muted-foreground"
                  >
                    Clear all
                  </Button>
                </Flex>
              )}
              <Text size="sm" className="text-muted-foreground">
                Add files the agent can reference or upload during this step.
              </Text>

              {attachmentIds.length > 0 && attachments.length > 0 && (
                <div className="mt-3 space-y-1">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-2 rounded-md bg-muted/30 px-2 py-1"
                    >
                      <TagBadge name={getFileTypeBadge(attachment.contentType)} color="gray" hideDot size="sm" />
                      <div className="flex-1 min-w-0">
                        <Text size="sm" className="truncate">
                          {attachment.filename}
                        </Text>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onPress={() =>
                          setLocalData((prev) => ({
                            ...prev,
                            attachmentIds: Array.isArray(prev.attachmentIds)
                              ? (prev.attachmentIds as string[]).filter((aid) => aid !== attachment.id)
                              : [],
                          }))
                        }
                        className="text-muted-foreground hover:text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3">
                <AttachmentPickerDialog
                  selectedIds={attachmentIds}
                  onChange={(nextIds) =>
                    setLocalData((prev) => ({ ...prev, attachmentIds: nextIds }))
                  }
                  triggerVariant="outline"
                  triggerSize="sm"
                  triggerLabel="Add attachment"
                />
              </div>
            </div>
          )}
        </ConfigSection>
      </div>
    </div>
  );
};
